import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeft, ArrowRight, X, ChevronRight, Play } from "lucide-react";
import axios from "axios";
import { MOBILE_BREAKPOINT, VIDEO, ICON_SIZE } from "../constants/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Video component optimized for mobile
function OptimizedVideo({ src, index }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer for visibility
  useEffect(() => {
    const currentContainer = containerRef.current;
    const currentVideo = videoRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && currentVideo && isPlaying) {
          currentVideo.pause();
          setIsPlaying(false);
          setShowPlayButton(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, [isPlaying]);

  // Auto-play on desktop when loaded and visible
  useEffect(() => {
    const currentVideo = videoRef.current;
    if (!isMobile && isLoaded && currentVideo) {
      const playPromise = currentVideo.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setShowPlayButton(false);
          })
          .catch(() => {
            setShowPlayButton(true);
          });
      }
    }
  }, [isLoaded, isMobile]);

  const handlePlayClick = useCallback((e) => {
    e.stopPropagation();
    const currentVideo = videoRef.current;
    if (!currentVideo) return;

    if (isPlaying) {
      currentVideo.pause();
      setIsPlaying(false);
      setShowPlayButton(true);
    } else {
      const playPromise = currentVideo.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            setShowPlayButton(false);
          })
          .catch(() => {
            // Silent fail - user interaction required
          });
      }
    }
  }, [isPlaying]);

  const handleFullscreen = useCallback(() => {
    const currentVideo = videoRef.current;
    if (!currentVideo) return;

    if (currentVideo.webkitEnterFullscreen) {
      currentVideo.webkitEnterFullscreen();
    } else if (currentVideo.requestFullscreen) {
      currentVideo.requestFullscreen();
    } else if (currentVideo.webkitRequestFullscreen) {
      currentVideo.webkitRequestFullscreen();
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    setShowPlayButton(false);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
    setShowPlayButton(true);
  }, []);

  const handleVideoLoaded = useCallback(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative border border-[#E5E5E0] hover:border-[#D4AF37] transition-colors duration-300 overflow-hidden cursor-pointer group bg-[#F0F0EB]"
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={handleVideoLoaded}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onClick={handlePlayClick}
        className={`w-full h-[${VIDEO.GALLERY_HEIGHT}px] object-cover`}
        style={{ height: VIDEO.GALLERY_HEIGHT }}
        data-testid={`realization-video-${index}`}
      >
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>
      
      {/* Loading spinner */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#F0F0EB]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      {/* Play button overlay */}
      {isLoaded && (showPlayButton || !isPlaying) && (
        <div 
          className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${
            isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          onClick={handlePlayClick}
        >
          <div className="bg-white/90 rounded-full p-4 shadow-lg">
            <Play size={ICON_SIZE.XXL} className="text-[#1A1A1A] ml-1" fill="#1A1A1A" />
          </div>
        </div>
      )}
      
      {/* Fullscreen button */}
      {isLoaded && isPlaying && (
        <button
          onClick={handleFullscreen}
          className="absolute bottom-3 right-3 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          aria-label="Plein écran"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
          </svg>
        </button>
      )}
    </div>
  );
}

// Brand Card Component
function BrandCard({ brand, onSelect }) {
  return (
    <button
      onClick={() => onSelect(brand)}
      data-testid={`brand-card-${brand.id}`}
      className="brand-card text-left"
    >
      <div>
        <h3 className="font-serif text-2xl md:text-3xl mb-4 text-[#1A1A1A]">
          {brand.name}
        </h3>
        <p className="text-[#666660] font-light text-sm leading-relaxed">
          {brand.description}
        </p>
      </div>
      <div className="flex items-center gap-2 text-[#D4AF37] mt-6">
        <span className="text-xs uppercase tracking-widest">
          {brand.models.length} modèles
        </span>
        <ArrowRight size={16} />
      </div>
    </button>
  );
}

// Model Card Component
function ModelCard({ model, onSelect }) {
  return (
    <button
      onClick={() => onSelect(model)}
      data-testid={`model-card-${model.id}`}
      className="model-card text-left border border-[#E5E5E0] overflow-hidden"
    >
      <img
        src={model.image}
        alt={model.name}
        className="w-full object-cover"
        style={{ height: VIDEO.MODEL_IMAGE_HEIGHT }}
      />
      <div className="model-card-overlay">
        <h4 className="font-serif text-xl text-white">
          {model.name}
        </h4>
      </div>
    </button>
  );
}

// Breadcrumb Component
function Breadcrumb({ items }) {
  return (
    <section className="bg-[#F0F0EB] py-4">
      <div className="container-premium">
        <div className="flex items-center gap-2 text-sm">
          {items.map((item, idx) => (
            <span key={`breadcrumb-${item}-${idx}`} className="flex items-center gap-2">
              {idx > 0 && <ChevronRight size={14} className="text-[#666660]" />}
              <span className={idx === items.length - 1 ? "text-[#1A1A1A]" : "text-[#666660]"}>
                {item}
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// Realization Gallery Component
function RealizationGallery({ realizations, onImageClick }) {
  if (realizations.length === 0) {
    return (
      <div className="text-center py-16 border border-[#E5E5E0]">
        <p className="text-[#666660] font-light">
          Aucune réalisation disponible pour ce modèle.
        </p>
        <p className="text-[#666660] font-light mt-2">
          Contactez-nous pour créer votre paire personnalisée.
        </p>
      </div>
    );
  }

  return (
    <>
      {realizations.map((realization) => (
        <div key={realization.id} className="mb-12">
          {realization.description && (
            <p className="text-[#666660] font-light mb-6 text-lg">
              {realization.description}
            </p>
          )}
          
          {/* Videos */}
          {realization.videos && realization.videos.length > 0 && (
            <div className="gallery-grid mb-6">
              {realization.videos.map((video, vidIdx) => (
                <OptimizedVideo 
                  key={`video-${realization.id}-${vidIdx}`}
                  src={video}
                  index={vidIdx}
                />
              ))}
            </div>
          )}

          {/* Images */}
          {realization.images && realization.images.length > 0 && (
            <div className="gallery-grid">
              {realization.images.map((image, imgIdx) => (
                <button
                  key={`img-${realization.id}-${imgIdx}`}
                  onClick={() => onImageClick(image)}
                  data-testid={`realization-image-${imgIdx}`}
                  className="image-zoom cursor-pointer border border-[#E5E5E0] hover:border-[#D4AF37] transition-colors duration-300"
                >
                  <img
                    src={image}
                    alt={`Réalisation ${imgIdx + 1}`}
                    className="w-full object-cover"
                    style={{ height: VIDEO.GALLERY_HEIGHT }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

// Lightbox Component
function Lightbox({ image, onClose }) {
  if (!image) return null;

  return (
    <div
      data-testid="lightbox"
      className="lightbox"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white hover:text-[#D4AF37] transition-colors duration-300"
        aria-label="Fermer"
      >
        <X size={ICON_SIZE.XXL} strokeWidth={1} />
      </button>
      <img
        src={image}
        alt="Réalisation en grand"
        className="max-w-[90%] max-h-[90vh] object-contain"
      />
    </div>
  );
}

export default function CustomizationPage() {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [realizations, setRealizations] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        setBrands(response.data);
      } catch (error) {
        // Error handled silently - brands remain empty
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  // Fetch realizations when model is selected
  useEffect(() => {
    if (!selectedBrand || !selectedModel) return;

    const fetchRealizations = async () => {
      try {
        const response = await axios.get(
          `${API}/realizations/${selectedBrand.id}/${selectedModel.id}`
        );
        setRealizations(response.data);
      } catch (error) {
        // Error handled silently - realizations remain empty
      }
    };
    fetchRealizations();
  }, [selectedBrand, selectedModel]);

  const handleBrandSelect = useCallback((brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setRealizations([]);
  }, []);

  const handleModelSelect = useCallback((model) => {
    setSelectedModel(model);
  }, []);

  const handleBack = useCallback(() => {
    if (selectedModel) {
      setSelectedModel(null);
      setRealizations([]);
    } else if (selectedBrand) {
      setSelectedBrand(null);
    }
  }, [selectedModel, selectedBrand]);

  const handleLightboxOpen = useCallback((image) => {
    setLightboxImage(image);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxImage(null);
  }, []);

  const getBreadcrumb = useCallback(() => {
    const items = ["Marques"];
    if (selectedBrand) items.push(selectedBrand.name);
    if (selectedModel) items.push(selectedModel.name);
    return items;
  }, [selectedBrand, selectedModel]);

  return (
    <div data-testid="customization-page" className="min-h-screen bg-[#F9F9F7]">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="max-w-3xl">
            <p className="caption text-[#D4AF37] mb-6">Nos Créations</p>
            <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-8 text-[#1A1A1A]">
              Personnalisation
            </h1>
            <p className="text-xl font-light text-[#666660] leading-relaxed">
              Explorez nos réalisations par marque et modèle. 
              Chaque création est unique, conçue avec passion et expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <Breadcrumb items={getBreadcrumb()} />

      {/* Content */}
      <section className="section-padding">
        <div className="container-premium">
          {/* Back button */}
          {(selectedBrand || selectedModel) && (
            <button
              onClick={handleBack}
              data-testid="back-button"
              className="flex items-center gap-2 text-[#666660] hover:text-[#1A1A1A] transition-colors duration-300 mb-8"
            >
              <ArrowLeft size={ICON_SIZE.MEDIUM} />
              <span className="text-sm uppercase tracking-widest">Retour</span>
            </button>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Brand Selection */}
          {!loading && !selectedBrand && (
            <div>
              <h2 className="font-serif text-3xl mb-8 text-[#1A1A1A]">
                Sélectionnez une marque
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {brands.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} onSelect={handleBrandSelect} />
                ))}
              </div>
            </div>
          )}

          {/* Model Selection */}
          {!loading && selectedBrand && !selectedModel && (
            <div>
              <h2 className="font-serif text-3xl mb-2 text-[#1A1A1A]">
                {selectedBrand.name}
              </h2>
              <p className="text-[#666660] font-light mb-8">
                {selectedBrand.description}
              </p>
              <h3 className="font-serif text-xl mb-6 text-[#1A1A1A]">
                Sélectionnez un modèle
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedBrand.models.map((model) => (
                  <ModelCard key={model.id} model={model} onSelect={handleModelSelect} />
                ))}
              </div>
            </div>
          )}

          {/* Realizations Gallery */}
          {!loading && selectedBrand && selectedModel && (
            <div>
              <h2 className="font-serif text-3xl mb-2 text-[#1A1A1A]">
                {selectedBrand.name} - {selectedModel.name}
              </h2>
              <p className="text-[#666660] font-light mb-8">
                Découvrez nos réalisations sur ce modèle
              </p>
              <RealizationGallery 
                realizations={realizations} 
                onImageClick={handleLightboxOpen} 
              />
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox image={lightboxImage} onClose={handleLightboxClose} />

      {/* CTA */}
      <section className="section-padding bg-[#F0F0EB]">
        <div className="container-premium">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl mb-6 text-[#1A1A1A]">
              Créez votre paire unique
            </h2>
            <p className="text-[#666660] font-light mb-8">
              Vous ne trouvez pas ce que vous cherchez ? 
              Prenez rendez-vous pour une consultation personnalisée.
            </p>
            <a
              href="/contact"
              data-testid="customization-cta"
              className="btn-primary inline-block"
            >
              Prendre Rendez-vous
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
